<script lang="ts" setup>
import { EffectFade, Controller, Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import SwiperCore from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-fade'
import Link1 from '@/components/Button/Link1.vue'
import Link2 from '@/components/Button/Link2.vue'
import Pagination2 from '@/components/Slider/Pagination2.vue'
import { type Product } from '@/utils/types'

defineProps({
  products: {
    type: Array<Product>,
    default: []
  },
  url: {
    type: String,
    default: ''
  }
})

SwiperCore.use([Autoplay, Pagination])

const currentIndex: Ref<number> = ref(0)

const swiperSlideSelector = ref()
const onSwiper = (data: any) => {
  //存放 swiper
  swiperSlideSelector.value = data
}

const swiperSlideSecondSelector = ref()
const onSwiperSecond = (data: any) => {
  //存放 swiper
  swiperSlideSecondSelector.value = data
}

const swiperTo = (index: number) => {
  swiperSlideSelector.value.slideTo(index)
}

const handelSwiperChange = (data: any) => {
  currentIndex.value = data.realIndex
  swiperSlideSecondSelector.value.slideTo(data.realIndex)
}

const handleSlidePrev = () => {
  swiperSlideSelector.value.slidePrev()
}
const handleSlideNext = () => {
  swiperSlideSelector.value.slideNext()
}

const handlePaginationChange = (index: number) => {
  swiperTo(index)
  currentIndex.value = index
}
</script>

<template>
  <div class="slide">
    <swiper :autoplay="{
      delay: 3000
    }" :slides-per-view="1" :space-between="0" :speed="300" :loop="true" :modules="[EffectFade]" effect="fade"
      class="swiper-list brand-slide-swiper" @swiper="onSwiper" @slide-change="handelSwiperChange">
      <!-- @swiper="onSwiper" -->
      <swiper-slide v-for="(item, index) in products" :key="index">
        <div class="swiper-list__box">
          <figure class="swiper-list__pic">
            <Nimg :src="item.image?.link" :alt="item.image?.alt" :title="item.image?.title" format="webp" />
          </figure>
          <div class="swiper-list__right">
            <div class="swiper-list__category">焦點推薦</div>
            <div class="swiper-list__title">{{ item.name }}</div>
            <Link1 :to="item.url" :title="item.name" color="gray-3" class="swiper-list__link">立即選購</Link1>
          </div>
        </div>
      </swiper-slide>
    </swiper>
    <div class="pagination-box" v-if="products.length > 1">
      <Pagination2 :currentIndex="currentIndex" @update:change="handlePaginationChange" :totalIndex="products.length"
        class="pagination-box__item" />
    </div>
    <div class="slide__bottom">
      <div class="arrow brand3">
        <div class="arrow__prev" @click="handleSlidePrev" v-if="products.length > 1">
          <svg-icon name="icon-arrow-right2" />
        </div>
        <div class="arrow__next" @click="handleSlideNext" v-if="products.length > 1">
          <svg-icon name="icon-arrow-right2" />
        </div>
      </div>
      <Link2 :to="url" linkTitle="品牌官網" class="slide__bottom__weblink circle brand3" v-if="!isEmpty(url)" />
    </div>
    <div class="slide__mobile">
      <swiper :controller="{ control: swiperSlideSecondSelector }" :modules="[Controller]" :autoplay="{
        delay: 3000
      }" :slides-per-view="1" :space-between="0" :speed="300" :loop="true" class="swiper-list-mobile brand-slide-swiper"
        @swiper="onSwiperSecond">
        <swiper-slide v-for="(item, index) in products" :key="index">
          <div class="swiper-list-mobile__box">
            <Link1 :to="item.url" :title="item.name" color="gray-3" class="swiper-list-mobile__link">立即選購</Link1>
          </div>
        </swiper-slide>
      </swiper>
      <Link2 :to="url" linkTitle="品牌官網" class="slide__mobile__weblink circle brand3" v-if="!isEmpty(url)" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pagination-box {
  width: 100%;
  max-width: 330px;

  @include max-media(580) {
    max-width: 100%;
    margin-top: toRem(24);
  }
}

.slide {
  position: relative;
  margin-top: toRem(80);
  margin-bottom: toRem(80);

  &__bottom {
    display: flex;
    justify-content: space-between;

    &__weblink {
      @include max-media(580) {
        display: none;
      }
    }
  }

  &__mobile {
    display: flex;
    justify-content: space-between;
    padding-top: toRem(30);

    @include min-media(581) {
      display: none;
    }

    &__weblink {
      flex-shrink: 0;
    }
  }
}

.swiper-list {
  @include min-media(581) {
    padding-bottom: toRem(120);
  }

  .swiper-slide {
    &-active {
      .swiper-list {
        &__box {
          opacity: 1;
        }

        &__category,
        &__title,
        &__link {
          opacity: 1;
          transform: translateY(toRem(0));
        }

        &__category {
          transition-delay: 0.1s;
        }

        &__title {
          transition-delay: 0.2s;
        }

        &__link {
          transition-delay: 0.3s;
        }
      }
    }
  }

  &__box {
    display: flex;
    justify-content: space-between;
    opacity: 0;

    @include max-media(580) {
      flex-direction: column-reverse;
    }

    .swiper-list {
      &__pic {
        max-width: toRem(330);

        @include max-media(580) {
          max-width: 100%;
        }
      }

      &__right {
        display: flex;
        flex-direction: column;
        max-width: toRem(450);
        padding-top: toRem(15);
        padding-left: toRem(20);

        @include max-media(580) {
          padding-left: 0;
          padding-top: 0;
        }
      }

      &__category,
      &__title,
      &__link {
        opacity: 0;
        transform: translateY(toRem(20));
        transition: all 0.6s ease-in-out;
      }

      &__title {
        margin-bottom: toRem(50);

        @include max-media(580) {
          margin-bottom: toRem(20);
        }
      }

      &__link {
        margin-top: auto;

        @include max-media(580) {
          display: none;
        }
      }
    }
  }

  &__pic {
    width: 100%;

    img {
      display: block;
      width: 100%;
    }
  }

  &__right {
    position: relative;
    width: 100%;
  }

  &__category {
    font-size: toRem(16);
  }

  &__title {
    font-size: toRem(28);
  }
}
</style>
<style lang="scss">
.brand-slide-swiper {
  .swiper-pagination {
    width: calc(100% - toRem(130));
    text-align: left;

    @include max-media(580) {
      position: relative;
      top: 0;
      bottom: 0;
      margin-top: toRem(18);
      margin-bottom: toRem(5);
    }

    .swiper-pagination-bullet {
      width: toRem(44);
    }
  }
}
</style>
